import * as React from "react";
import Downshift from "downshift";
import styled from "styled-components";

import { MenuLayer } from "@hubper/core-ui/components";
import { SelectMenuItem, SelectMenu } from "@hubper/core-ui/elements";
import highlightText from "@hubper/core-ui/util/highlightText";
import { PoseGroup } from "react-pose";

import Input from "../components/Input";

const Base = styled.div`
  height: 100%;
  flex: 1 1;
`;

type Props = {
  style?: React.CSSProperties;
  className?: string;
  items: string[];
  placeholder?: string;
  onChange?: (value: string) => void;
  value?: string;
};

function String(
  { style, className, items, placeholder, onChange, value }: Props,
  ref: any
) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current!.focus();
    }
  }));

  return (
    <Downshift
      inputValue={value}
      selectedItem={value}
      onChange={onChange}
      onInputValueChange={onChange}
    >
      {({
        getRootProps,
        getInputProps,
        isOpen,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        inputValue
      }) => {
        let filteredItems = [] as string[];
        if (inputValue) {
          const lc = inputValue.toLowerCase();
          filteredItems = items
            .filter(item => item.toLowerCase().includes(lc))
            .sort(
              (a, b) =>
                a.toLowerCase().indexOf(lc) - b.toLowerCase().indexOf(lc)
            )
            .slice(0, 5);
        }

        return (
          <Base
            {...getRootProps({ refKey: "ref" })}
            style={style}
            className={className}
            data-testid="string-filter"
          >
            <>
              <Input
                isOpen={isOpen && filteredItems.length > 0}
                placeholder={placeholder}
                isDropDown
                {...getInputProps({ ref: inputRef }) as any}
              />
              <MenuLayer element={inputRef.current}>
                {style => (
                  <PoseGroup flipMove={false}>
                    {isOpen && filteredItems.length > 0 && (
                      <SelectMenu
                        key="menu"
                        {...getMenuProps({
                          style
                        })}
                      >
                        {filteredItems.map((item, index) => {
                          return (
                            <SelectMenuItem
                              {...getItemProps({
                                item,
                                index
                              })}
                              key={item}
                              style={{ padding: "4px 12px" }}
                              highlighted={index === highlightedIndex}
                            >
                              {highlightText(item, inputValue || "")}
                            </SelectMenuItem>
                          );
                        })}
                      </SelectMenu>
                    )}
                  </PoseGroup>
                )}
              </MenuLayer>
            </>
          </Base>
        );
      }}
    </Downshift>
  );
}

export default React.forwardRef(String);
